import {scaleLinear, scaleOrdinal} from '@visx/scale';
import {ScaleOrdinal, ScaleLinear, extent} from 'd3';

import {assert} from 'Asserts';
import {AnalyticsVisualizationColorPalette as ColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  areLabeledDatasets,
  Dataset,
  Datasets,
  isLabeledDataset,
  LabelCompatibleType,
} from 'ui-library/charting/Types';
import {assertNever} from 'util/Types';

import {
  CHART_COLORS_DISCRETE_PALETTE,
  CHART_COLORS_GRADIENT_PALETTE,
  CHART_COLORS_NO_LABEL_COLOR,
} from './Theme';

export type DiscreteColorScale = ScaleOrdinal<LabelCompatibleType, string>;
export type GradientColorScale = ScaleLinear<string, string>;
export type ColorScale = DiscreteColorScale | GradientColorScale;

export interface DiscreteSeriesColorConfig {
  colorPalette: ColorPalette.DISCRETE;
  colorScale: DiscreteColorScale;
}
export interface GradientSeriesColorConfig {
  colorPalette: ColorPalette.GRADIENT;
  colorScale: GradientColorScale;
}
export type SeriesColorConfig = DiscreteSeriesColorConfig | GradientSeriesColorConfig;

export function getGradientColorScale(labels: Array<number>): GradientColorScale {
  return scaleLinear({
    domain: extent(labels) as [number, number],
    range: CHART_COLORS_GRADIENT_PALETTE,
  });
}

export function getDiscreteColorScale(labels: Array<LabelCompatibleType>): DiscreteColorScale {
  return scaleOrdinal({
    domain: labels,
    range: CHART_COLORS_DISCRETE_PALETTE,
  });
}

export function getColorForDataset(dataset: Dataset, colorConfig: SeriesColorConfig): string {
  if (!isLabeledDataset(dataset)) {
    return CHART_COLORS_NO_LABEL_COLOR;
  } else if (colorConfig.colorPalette === ColorPalette.DISCRETE) {
    return colorConfig.colorScale(dataset.label as LabelCompatibleType);
  } else {
    return colorConfig.colorScale(dataset.label as number);
  }
}

export default function getSeriesColorConfig(
  datasets: Datasets,
  colorPalette: ColorPalette
): SeriesColorConfig {
  if (!areLabeledDatasets(datasets)) {
    return {
      colorPalette: ColorPalette.DISCRETE,
      colorScale: getDiscreteColorScale([]),
    };
  }
  switch (colorPalette) {
    case ColorPalette.DISCRETE: {
      const colorScale = getDiscreteColorScale(
        datasets.map((dataset) => dataset.label as LabelCompatibleType)
      );
      return {
        colorPalette,
        colorScale,
      };
    }
    case ColorPalette.GRADIENT: {
      const colorScale = getGradientColorScale(datasets.map((dataset) => dataset.label as number));
      return {
        colorPalette,
        colorScale,
      };
    }
    default:
      assert(false, `Got unexpected color palette ${assertNever(colorPalette)}`);
  }
}
